/**
 * EmailLayout Component
 *
 * Main container for email module with 3-pane layout and nested routes.
 * Handles routing for threads, compose, drafts, and search.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  EmailAccount,
  EmailMessage,
  EmailDraft,
  draftsApi,
} from '@/services/emailApi';
import EmailAccountSetup from '@/components/modules/EmailAccountSetup';
import EmailCompose, { ComposeMode } from '@/components/modules/EmailCompose';
import EmailThreadView from '@/components/modules/EmailThreadView';
import AccountSidebar from './AccountSidebar';
import ThreadList from './ThreadList';
import AIDashboard from './AIDashboard';
import SearchResults from './SearchResults';
import { useEmailData, ThreadFilterMode } from './hooks/useEmailData';

const EmailLayout: React.FC = () => {
  const { t } = useTranslation('email');
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ accountId?: string; folderId?: string; threadId?: string }>();

  // Get URL params
  const urlAccountId = params.accountId;
  const urlFolderId = params.folderId;
  const urlThreadId = params.threadId;

  // Check if viewing thread overlay or search
  const isThreadView = location.pathname.includes('/email/thread/');
  const isDraftsView = location.pathname === '/email/drafts';
  const isSearchView = location.pathname.includes('/email/search');

  // Filter state (must be declared before useEmailData)
  // Default to 'actionable' for needs action / priority-based workflow
  const [filterMode, setFilterMode] = useState<ThreadFilterMode>('actionable');

  // Use email data hook
  const emailData = useEmailData({
    accountId: urlAccountId,
    folderId: urlFolderId,
    filterMode,
  });

  const {
    accounts,
    accountFolders,
    expandedAccounts,
    selectedAccountId,
    selectedFolderId,
    folders,
    threads,
    totalCount,
    hasNextPage,
    drafts,
    loadingDrafts,
    aiDashboard,
    hasAIDashboard,
    loadingDashboard,
    loading,
    loadingThreads,
    isLoadingMore,
    loadMoreThreads,
    toggleAccountExpand,
    selectFolder,
    markThreadAsRead,
    addAccount,
    loadDrafts,
    deleteDraftLocal,
  } = emailData;

  // UI State
  const [showSidebar, setShowSidebar] = useState(false);
  const [showReading, setShowReading] = useState(false);
  const [showAccountSetup, setShowAccountSetup] = useState(false);

  // Compose state (for non-route compose)
  const [showCompose, setShowCompose] = useState(false);
  const [composeMode, setComposeMode] = useState<ComposeMode>('new');
  const [composeMessage, setComposeMessage] = useState<EmailMessage | null>(null);

  // Draft state
  const [selectedDraft, setSelectedDraft] = useState<EmailDraft | null>(null);
  const [showDraftsViewLocal, setShowDraftsViewLocal] = useState(isDraftsView);

  // Sync drafts view with URL
  useEffect(() => {
    setShowDraftsViewLocal(isDraftsView);
    if (isDraftsView && selectedAccountId) {
      loadDrafts(selectedAccountId);
    }
  }, [isDraftsView, selectedAccountId, loadDrafts]);

  // Handle folder selection with navigation
  const handleSelectFolder = useCallback((accountId: string, folderId: string) => {
    selectFolder(accountId, folderId);
    setShowSidebar(false);
    setShowReading(false);
    setSelectedDraft(null);
    setFilterMode('all'); // Reset filter when changing folder

    // Check if this is the drafts folder
    const folder = (accountFolders[accountId] || []).find(f => f.id === folderId);
    if (folder?.folder_type === 'drafts') {
      navigate('/email/drafts');
    } else {
      navigate(`/email/account/${accountId}/folder/${folderId}`);
    }
  }, [selectFolder, accountFolders, navigate]);

  // Handle thread selection with navigation
  const handleSelectThread = useCallback((thread: { id: string }) => {
    setShowReading(true);
    navigate(`/email/thread/${thread.id}`);
  }, [navigate]);

  // Handle thread close
  const handleCloseThread = useCallback(() => {
    setShowReading(false);
    navigate(-1);
  }, [navigate]);

  // Handle account added
  const handleAccountAdded = useCallback(async (account: EmailAccount) => {
    setShowAccountSetup(false);
    addAccount(account);
  }, [addAccount]);

  // Get unread count for folder
  const getUnreadCount = useCallback((folderId?: string) => {
    if (!folderId) return threads.filter(t => t.unread_count > 0).length;
    return folders.find(f => f.id === folderId)?.unread_count || 0;
  }, [threads, folders]);

  // Compose handlers
  const handleOpenCompose = useCallback(() => {
    setComposeMode('new');
    setComposeMessage(null);
    setSelectedDraft(null);
    setShowCompose(true);
  }, []);

  const handleReply = useCallback((message: EmailMessage) => {
    setComposeMode('reply');
    setComposeMessage(message);
    setShowCompose(true);
  }, []);

  const handleReplyAll = useCallback((message: EmailMessage) => {
    setComposeMode('reply-all');
    setComposeMessage(message);
    setShowCompose(true);
  }, []);

  const handleForward = useCallback((message: EmailMessage) => {
    setComposeMode('forward');
    setComposeMessage(message);
    setShowCompose(true);
  }, []);

  const handleEmailSent = useCallback((_messageId: string) => {
    // Refresh threads to show sent email
    emailData.loadThreads(1, true);
  }, [emailData]);

  // Draft handlers
  const handleEditDraft = useCallback((draft: EmailDraft) => {
    setSelectedDraft(draft);
    setComposeMode('draft');
    setShowCompose(true);
  }, []);

  const handleDeleteDraft = useCallback(async (draftId: string) => {
    if (!confirm(t('compose.delete_draft_confirm'))) return;
    try {
      await draftsApi.delete(draftId);
      deleteDraftLocal(draftId);
      if (selectedDraft?.draft_id === draftId) {
        setSelectedDraft(null);
      }
    } catch (err) {
      console.error('Failed to delete draft:', err);
      alert(t('compose.delete_draft_failed'));
    }
  }, [deleteDraftLocal, selectedDraft]);

  const handleDraftSaved = useCallback((_draftId: string) => {
    if (showDraftsViewLocal && selectedAccountId) {
      loadDrafts(selectedAccountId);
    }
  }, [showDraftsViewLocal, selectedAccountId, loadDrafts]);

  const handleDraftDeleted = useCallback((draftId: string) => {
    deleteDraftLocal(draftId);
    if (selectedDraft?.draft_id === draftId) {
      setSelectedDraft(null);
    }
  }, [deleteDraftLocal, selectedDraft]);

  // Search view - render SearchResults component
  if (isSearchView) {
    return <SearchResults />;
  }

  // No accounts - show setup prompt
  if (!loading && accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('account_setup.connect_your_email')}</h2>
          <p className="text-gray-600 mb-6">
            {t('account_setup.add_email_account_description')}
          </p>
          <button
            onClick={() => setShowAccountSetup(true)}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
          >
            {t('account_setup.add_email_account')}
          </button>
          <button
            onClick={() => navigate('/voice')}
            className="w-full py-2 mt-3 text-gray-600 hover:text-gray-900 text-sm"
          >
            {t('navigation.go_back')}
          </button>
        </div>

        {showAccountSetup && (
          <EmailAccountSetup
            onComplete={handleAccountAdded}
            onCancel={() => setShowAccountSetup(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-100" style={{ height: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Top Header */}
      <div className="bg-blue-600 text-white px-2 sm:px-4 py-2 flex items-center justify-between shadow-md flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="lg:hidden p-1.5 hover:bg-blue-700 rounded transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button onClick={() => navigate('/voice')} className="p-1.5 hover:bg-blue-700 rounded transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base sm:text-lg font-semibold">{t('navigation.mail')}</h1>
        </div>

        <div className="flex-1 max-w-2xl mx-2 sm:mx-4">
          <form
            className="relative flex"
            onSubmit={(e) => {
              e.preventDefault();
              const input = e.currentTarget.querySelector('input');
              if (input && input.value.trim()) {
                navigate(`/email/search?q=${encodeURIComponent(input.value.trim())}`);
              }
            }}
          >
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('search.search_mail')}
                className="w-full pl-10 pr-4 py-1.5 bg-blue-700 text-white placeholder-blue-300 rounded-l border border-blue-500 focus:bg-white focus:text-gray-900 focus:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white text-sm"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-1.5 bg-blue-800 hover:bg-blue-900 text-white rounded-r border border-l-0 border-blue-500 text-sm font-medium transition"
            >
              {t('search.search')}
            </button>
          </form>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAccountSetup(true)}
            className="p-1.5 hover:bg-blue-700 rounded transition"
            title={t('account_setup.add_account')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={handleOpenCompose}
            className="bg-white text-blue-600 px-2 sm:px-4 py-1.5 rounded font-medium hover:bg-blue-50 transition text-xs sm:text-sm flex items-center gap-1 sm:gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <span className="hidden sm:inline">{t('compose.compose')}</span>
          </button>
        </div>
      </div>

      {/* Main Three-Pane Layout */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* LEFT PANE: Account Sidebar */}
        <AccountSidebar
          accounts={accounts}
          accountFolders={accountFolders}
          expandedAccounts={expandedAccounts}
          selectedAccountId={selectedAccountId}
          selectedFolderId={selectedFolderId}
          showSidebar={showSidebar}
          onToggleSidebar={() => setShowSidebar(false)}
          onToggleAccountExpand={toggleAccountExpand}
          onSelectFolder={handleSelectFolder}
          onAddAccount={() => setShowAccountSetup(true)}
        />

        {/* MIDDLE PANE: Thread List */}
        <ThreadList
          threads={threads}
          folders={folders}
          selectedFolderId={selectedFolderId}
          selectedThreadId={urlThreadId}
          selectedDraftId={selectedDraft?.draft_id || null}
          totalCount={totalCount}
          hasNextPage={hasNextPage}
          loading={loadingThreads}
          isLoadingMore={isLoadingMore}
          showReading={showReading || isThreadView}
          showDraftsView={showDraftsViewLocal}
          drafts={drafts}
          loadingDrafts={loadingDrafts}
          filterMode={filterMode}
          onFilterChange={setFilterMode}
          onSelectThread={handleSelectThread}
          onLoadMore={loadMoreThreads}
          onEditDraft={handleEditDraft}
          onDeleteDraft={handleDeleteDraft}
          getUnreadCount={getUnreadCount}
        />

        {/* RIGHT PANE: Reading Pane / AI Dashboard */}
        <div className={`
          ${showReading || isThreadView ? 'flex' : 'hidden md:flex'}
          flex-1 bg-white flex-col overflow-hidden
        `}>
          {isThreadView && urlThreadId ? (
            <EmailThreadView
              threadId={urlThreadId}
              onClose={handleCloseThread}
              onReply={handleReply}
              onReplyAll={handleReplyAll}
              onForward={handleForward}
              onMarkRead={markThreadAsRead}
            />
          ) : (
            <AIDashboard
              aiDashboard={aiDashboard}
              hasAIDashboard={!!hasAIDashboard}
              loadingDashboard={loadingDashboard}
              onSelectThread={(id) => handleSelectThread({ id })}
            />
          )}
        </div>

        {/* Outlet for nested routes (overlays) */}
        <Outlet />
      </div>

      {/* Account Setup Modal */}
      {showAccountSetup && (
        <EmailAccountSetup
          onComplete={handleAccountAdded}
          onCancel={() => setShowAccountSetup(false)}
        />
      )}

      {/* Compose Modal */}
      {showCompose && selectedAccountId && (
        <EmailCompose
          isOpen={showCompose}
          onClose={() => {
            setShowCompose(false);
            setSelectedDraft(null);
          }}
          mode={composeMode}
          messageId={composeMessage?.id}
          originalMessage={composeMessage ? {
            from_email: composeMessage.from_email,
            from_name: composeMessage.from_name,
            to: composeMessage.to,
            cc: composeMessage.cc,
            subject: composeMessage.subject,
            body_text: composeMessage.body_text,
            body_html: composeMessage.body_html,
            received_at: composeMessage.received_at,
          } : undefined}
          selectedAccountId={selectedAccountId}
          onSent={handleEmailSent}
          editDraft={selectedDraft || undefined}
          onDraftSaved={handleDraftSaved}
          onDraftDeleted={handleDraftDeleted}
        />
      )}
    </div>
  );
};

export default EmailLayout;
